import { useState } from 'react';
import * as Sentry from '@sentry/browser';
import { loginSsoUser } from './api/api';
import { dtoToModel } from './api/mapper';

export enum FetchStatus {
  INITIAL,
  FETCHING,
  SUCCESS,
  ERROR,
}

interface SignInProps {
  status: FetchStatus;
  nextUrl: string;
  fieldErrors: SignInValidationErrors | null;
  loginWithSso: (email: string, loginId: string) => void;
  apiErrorMessage: SignInGenericError;
}

type SignInValidationErrors = {
  email?: string[];
  loginId?: string[];
};

type SignInGenericError = string | null;

const genericErrorMessage = 'Oops. Something went wrong.';

export function useSsoSignIn(): SignInProps {
  const [status, setStatus] = useState<FetchStatus>(FetchStatus.INITIAL);
  const [fieldErrors, setFieldErrors] = useState<SignInValidationErrors | null>(null);
  const [apiErrorMessage, setApiError] = useState<SignInGenericError>(null);
  const [url, setNextUrl] = useState('');
  const clearErrors = () => setFieldErrors(null);

  const loginWithSso = async (email: string, loginId: string) => {
    if (!email || !loginId) return;
    setStatus(FetchStatus.FETCHING);
    clearErrors();

    try {
      const { data } = await loginSsoUser({ email, loginId });
      setStatus(FetchStatus.SUCCESS);
      if (data) {
        const { nextUrl } = dtoToModel(data);
        setNextUrl(nextUrl);
      }
    } catch (responseError) {
      setStatus(FetchStatus.ERROR);

      const { errors, error }: { errors: SignInValidationErrors; error: SignInGenericError } =
        // @ts-expect-error fix catch error type
        responseError?.response?.data || {};
      if (errors) {
        setFieldErrors(errors);
      } else if (error) {
        setApiError(error);
      } else {
        setApiError(genericErrorMessage);
        Sentry.captureMessage('Error signing in user with SSO', Sentry.Severity.Error);
        Sentry.captureException(responseError);
      }
    }
  };

  return {
    fieldErrors,
    status,
    nextUrl: url,
    loginWithSso,
    apiErrorMessage,
  };
}
